/**
 * Swahili Language Profile
 *
 * SVO word order, prepositions, space-separated, agglutinative.
 * Features noun class system (18 classes) and verb agreement prefixes.
 */

import type { LanguageProfile } from './types';

export const swahiliProfile: LanguageProfile = {
  code: 'sw',
  name: 'Swahili',
  nativeName: 'Kiswahili',
  direction: 'ltr',
  script: 'latin',
  wordOrder: 'SVO',
  markingStrategy: 'preposition',
  usesSpaces: true,
  verb: {
    position: 'start',
    subjectDrop: true,
  },
  references: {
    me: 'mimi', // "I/me"
    it: 'hiyo', // "it"
    you: 'wewe', // "you"
    result: 'matokeo',
    event: 'tukio',
    target: 'lengo',
    body: 'mwili',
    document: 'hati',
    window: 'dirisha',
    detail: 'maelezo',
  },
  possessive: {
    marker: '', // Swahili uses possessive pronouns
    markerPosition: 'after-object',
    usePossessiveAdjectives: true,
    specialForms: {
      me: 'yangu', // "my" (class-dependent: wangu/yangu/langu...)
      it: 'yake', // "its"
      you: 'yako', // "your"
    },
    keywords: {
      // "my" variants (noun class dependent)
      wangu: 'me', // M-Wa class (people)
      yangu: 'me', // N class (general)
      langu: 'me', // Ji-Ma class
      changu: 'me', // Ki-Vi class
      // "your" variants
      wako: 'you',
      yako: 'you',
      lako: 'you',
      chako: 'you',
      // "its/his/her" variants
      wake: 'it',
      yake: 'it',
      lake: 'it',
      chake: 'it',
    },
  },
  roleMarkers: {
    destination: { primary: 'kwenye', alternatives: ['kwa'], position: 'before' },
    source: { primary: 'kutoka', position: 'before' },
    patient: { primary: '', position: 'before' },
    style: { primary: 'na', position: 'before' },
  },
  keywords: {
    toggle: { primary: 'badilisha', normalized: 'toggle' },
    add: { primary: 'ongeza', normalized: 'add' },
    remove: { primary: 'ondoa', alternatives: ['futa', 'toa'], normalized: 'remove' },
    put: { primary: 'weka', alternatives: ['tia'], normalized: 'put' },
    append: { primary: 'ambatanisha', normalized: 'append' },
    prepend: { primary: 'tanguliza', normalized: 'prepend' },
    take: { primary: 'chukua', normalized: 'take' },
    make: { primary: 'tengeneza', alternatives: ['unda'], normalized: 'make' },
    clone: { primary: 'nakili', alternatives: ['rudufu'], normalized: 'clone' },
    swap: { primary: 'badilishana', alternatives: ['badalisha'], normalized: 'swap' },
    morph: { primary: 'geuza', alternatives: ['badilisha umbo'], normalized: 'morph' },
    set: { primary: 'seti', normalized: 'set' },
    get: { primary: 'pata', alternatives: ['pokea'], normalized: 'get' },
    increment: { primary: 'ongezeko', normalized: 'increment' },
    decrement: { primary: 'punguza', normalized: 'decrement' },
    log: { primary: 'andika', alternatives: ['rekodi'], normalized: 'log' },
    show: { primary: 'onyesha', normalized: 'show' },
    hide: { primary: 'ficha', alternatives: ['mficho'], normalized: 'hide' },
    // 'mpito' is what the i18n transformer renders (dict `transition: 'mpito'`);
    // without it the corpus transition clause never anchors (NO-TRANSITION sw).
    transition: {
      primary: 'hamisha',
      alternatives: ['animisha', 'mpito'],
      normalized: 'transition',
    },
    on: { primary: 'unapo', alternatives: ['kwenye'], normalized: 'on' },
    trigger: { primary: 'chochea', normalized: 'trigger' },
    send: { primary: 'tuma', alternatives: ['peleka'], normalized: 'send' },
    focus: { primary: 'lenga', alternatives: ['angazia'], normalized: 'focus' },
    blur: { primary: 'blur', normalized: 'blur' },
    // Phase 1 (v0.9.90): DOM / form state / debug
    empty: { primary: 'toa-kila-kitu', alternatives: ['mwaga', 'tupu'], normalized: 'empty' },
    open: { primary: 'fungua', normalized: 'open' },
    close: { primary: 'funga', normalized: 'close' },
    select: { primary: 'alama', normalized: 'select' },
    clear: { primary: 'safisha', normalized: 'clear' },
    reset: { primary: 'anzisha-upya', normalized: 'reset' },
    breakpoint: { primary: 'nukta-simama', normalized: 'breakpoint' },
    go: { primary: 'nenda', alternatives: ['enda', 'elekea'], normalized: 'go' },
    scroll: { primary: 'sogeza', alternatives: ['skroli'], normalized: 'scroll' },
    push: { primary: 'sukuma', alternatives: ['push'], normalized: 'push' },
    replace: { primary: 'badilisha_url', alternatives: ['badili_url'], normalized: 'replace' },
    process: { primary: 'shughulikia', alternatives: ['changanua'], normalized: 'process' },
    wait: { primary: 'subiri', alternatives: ['ngoja'], normalized: 'wait' },
    fetch: { primary: 'leta', alternatives: ['pakia'], normalized: 'fetch' },
    settle: { primary: 'tulia', alternatives: ['imarika'], normalized: 'settle' },
    if: { primary: 'kama', alternatives: ['ikiwa'], normalized: 'if' },
    unless: { primary: 'isipokuwa', normalized: 'unless' },
    when: { primary: 'wakati', normalized: 'when' },
    // Reactive `when <expr> changes` trigger word — synced VERBATIM from the i18n
    // dictionary (`changes`), which is what wrote every stored corpus row; the V1
    // vocab gate requires the two surfaces to agree. Native review pending — see
    // NATIVE_REVIEW_NEEDED.md § "Reactive `when … changes`".
    changes: { primary: 'inabadilika', normalized: 'changes' },
    where: { primary: 'wapi', normalized: 'where' },
    else: { primary: 'vinginevyo', alternatives: ['sivyo'], normalized: 'else' },
    repeat: { primary: 'rudia', normalized: 'repeat' },
    for: { primary: 'kwa', normalized: 'for' },
    while: { primary: 'kadri', normalized: 'while' },
    // `repeat forever` loop keyword — corpus word recognized so loopType types
    // as `:literal` like EN (the repeat.loopType R1 residue; see spanish.ts).
    forever: { primary: 'forever', normalized: 'forever' },
    continue: { primary: 'endelea', normalized: 'continue' },
    halt: { primary: 'simama', alternatives: ['acha'], normalized: 'halt' },
    throw: { primary: 'tupa', normalized: 'throw' },
    call: { primary: 'ita', normalized: 'call' },
    return: { primary: 'rudisha', alternatives: ['rejea'], normalized: 'return' },
    then: { primary: 'kisha', alternatives: ['halafu', 'baadaye'], normalized: 'then' },
    and: { primary: 'na', alternatives: ['pia', 'vilevile'], normalized: 'and' },
    // Predicate keyword (conditionals) — mirrors the Spanish profile. `ni` is the
    // Swahili copula ("is"); only recognized in predicate position (after a value,
    // before an adjective like `tupu`), so it doesn't disturb command parsing.
    is: { primary: 'ni', normalized: 'is' },
    // Comparison operator (`target matches .x`). Without this keyword the surface
    // stays an identifier and leaks verbatim into the condition's raw expression,
    // which the core expression parser reads as English (modal-close-backdrop /
    // focus-trap drop their then-branch). Not an ActionType and has no command
    // schema, so no pattern is generated from it.
    matches: { primary: 'inafanana', normalized: 'matches' },
    // Negative-existence operator (`if no dragHandle set dragHandle to me`). Same
    // seam as `exists`: without the keyword the surface stays an identifier and
    // leaks verbatim into the condition's raw expression (behavior-draggable).
    // Neither an ActionType nor a command schema, so no pattern is generated from it.
    no: { primary: 'hakuna', normalized: 'no' },
    end: { primary: 'mwisho', alternatives: ['maliza', 'tamati'], normalized: 'end' },
    js: { primary: 'js', alternatives: ['javascript'], normalized: 'js' },
    async: { primary: 'isiyo sawia', normalized: 'async' },
    tell: { primary: 'sema', alternatives: ['ambia'], normalized: 'tell' },
    // The sw dict renders default as bare `msingi` (chaguo-msingi is the full
    // compound); both accepted.
    default: { primary: 'chaguo-msingi', alternatives: ['msingi'], normalized: 'default' },
    init: { primary: 'anzisha', alternatives: ['anza'], normalized: 'init' },
    behavior: { primary: 'tabia', normalized: 'behavior' },
    install: { primary: 'sakinisha', normalized: 'install' },
    measure: { primary: 'pima', normalized: 'measure' },
    beep: { primary: 'lia', normalized: 'beep' },
    break: { primary: 'vunja', normalized: 'break' },
    copy: { primary: 'nakala', normalized: 'copy' },
    exit: { primary: 'toka', normalized: 'exit' },
    pick: { primary: 'chagua', normalized: 'pick' },
    render: { primary: 'chora', normalized: 'render' },
    into: { primary: 'ndani', normalized: 'into' },
    before: { primary: 'kabla', normalized: 'before' },
    after: { primary: 'baada', normalized: 'after' },
    // Event modifiers (for repeat until event)
    until: { primary: 'hadi', normalized: 'until' },
    event: { primary: 'tukio', normalized: 'event' },
    from: { primary: 'kutoka', normalized: 'from' },
    // Phase 8 (htmx v4 localized attributes): attribute-suffix keywords
    // used by the vocab generator — `sse-unganisha`, `hx-moja-kwa-moja`, etc.
    connect: { primary: 'unganisha', alternatives: ['unga', 'ungana'], normalized: 'connect' },
    stream: { primary: 'tiririsha', alternatives: ['mtiririko', 'strimu'], normalized: 'stream' },
    live: { primary: 'moja-kwa-moja', alternatives: ['mubashara', 'hai'], normalized: 'live' },
    socket: { primary: 'soketi', alternatives: ['tundu', 'soketi-wavuti'], normalized: 'socket' },
    // Reactive / realtime commands. `funga` is avoided for bind (it is the
    // `close` keyword); `ambatisha` (attach) is used instead.
    bind: { primary: 'ambatisha', alternatives: ['funganisha', 'bind'], normalized: 'bind' },
    intercept: { primary: 'zuia', alternatives: ['nasa', 'intercept'], normalized: 'intercept' },
    worker: { primary: 'mfanyakazi', alternatives: ['worker'], normalized: 'worker' },
    eventsource: {
      primary: 'eventsource',
      alternatives: ['chanzo-matukio'],
      normalized: 'eventsource',
    },
  },
  eventHandler: {
    keyword: { primary: 'unapo', alternatives: ['kwenye', 'kwa', 'wakati'] },
    sourceMarker: { primary: 'kutoka', position: 'before' },
    conditionalKeyword: { primary: 'unapo', alternatives: ['anapo', 'tunapo', 'mnapo', 'wanapo'] },
    eventMarker: {
      primary: 'unapo',
      alternatives: ['kwenye', 'kwa', 'wakati'],
      position: 'before',
    },
  },
};
